import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  VStack,
  HStack,
  Heading,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Alert,
  AlertIcon,
  useToast,
  TableContainer,
  Select,
  Text,
  Spinner,
} from "@chakra-ui/react";
import { EditIcon, ViewIcon } from "@chakra-ui/icons";
import { adminApiService } from "../services/adminApiService";
import { BlokInspeksi, BlokInspeksiQueryParams } from "../types";
import { FaSync } from "react-icons/fa";

const BlokInspeksiManagement: React.FC = () => {
  const [blokInspeksis, setBlokInspeksis] = useState<BlokInspeksi[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedBlokInspeksi, setSelectedBlokInspeksi] = useState<BlokInspeksi | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [syncStatusFilter, setSyncStatusFilter] = useState<string>("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const toast = useToast();

  // Fetch blok inspeksi data
  const fetchBlokInspeksis = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: BlokInspeksiQueryParams = {
        search: searchTerm || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        is_synced: syncStatusFilter === "all" ? undefined : syncStatusFilter === "synced",
      };
      const response = await adminApiService.getInspeksiList(params);
      setBlokInspeksis(response.data);
    } catch (error: any) {
      setError("Failed to fetch blok inspeksi data");
      console.error("Error fetching blok inspeksi:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlokInspeksis();
  }, [searchTerm, syncStatusFilter, startDate, endDate]);

  const handleViewDetails = (blokInspeksi: BlokInspeksi) => {
    setSelectedBlokInspeksi(blokInspeksi);
    onOpen();
  };

  const handleUpdateSyncStatus = async (blokInspeksi: BlokInspeksi, newStatus: boolean) => {
    setSubmitLoading(true);
    try {
      await adminApiService.updateInspeksiSyncStatus(blokInspeksi.uuid, newStatus);
      toast({
        title: "Success",
        description: `Sync status updated to ${newStatus ? "synced" : "unsynced"}`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      fetchBlokInspeksis();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to update sync status: ${error.response?.data?.detail || error.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error("Error updating sync status:", error);
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <Box p={6} textAlign="center">
        <Spinner size="lg" />
        <Text mt={4}>Loading blok inspeksi data...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="lg" mb={4}>Blok Inspeksi Management</Heading>
          
          <VStack spacing={4} align="stretch">
            <HStack spacing={4}>
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                maxW="300px"
              />
              <Select
                value={syncStatusFilter}
                onChange={(e) => setSyncStatusFilter(e.target.value)}
                maxW="200px"
              >
                <option value="all">All Status</option>
                <option value="synced">Synced</option>
                <option value="unsynced">Unsynced</option>
              </Select>
            </HStack>
            
            <HStack spacing={4}>
              <FormControl maxW="200px">
                <FormLabel fontSize="sm">Start Date</FormLabel>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  size="sm"
                />
              </FormControl>
              <FormControl maxW="200px">
                <FormLabel fontSize="sm">End Date</FormLabel>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  size="sm"
                />
              </FormControl>
              <Button 
                colorScheme="blue" 
                variant="outline"
                onClick={fetchBlokInspeksis}
                isLoading={loading}
                alignSelf="end"
              >
                Refresh
              </Button>
            </HStack>
          </VStack>
        </Box>

        <TableContainer>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>UUID</Th>
                <Th>Employee Name</Th>
                <Th>Employee ID</Th>
                <Th>Sync Source</Th>
                <Th>Last Updated</Th>
                <Th>Sync Status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {blokInspeksis.map((blokInspeksi) => (
                <Tr key={blokInspeksi.uuid}>
                  <Td>
                    <Badge colorScheme="blue" variant="outline" fontSize="xs">
                      {blokInspeksi.uuid.substring(0, 8)}...
                    </Badge>
                  </Td>
                  <Td>{blokInspeksi.nama || "-"}</Td>
                  <Td>
                    <Badge colorScheme="purple" variant="solid">
                      {blokInspeksi.nomor_induk_pegawai || "-"}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme="gray" variant="outline">
                      {blokInspeksi.sync_source}
                    </Badge>
                  </Td>
                  <Td>
                    <Text fontSize="sm">
                      {new Date(blokInspeksi.updated_at).toLocaleString()}
                    </Text>
                  </Td>
                  <Td>
                    <Badge colorScheme={blokInspeksi.is_synced ? "green" : "orange"}>
                      {blokInspeksi.is_synced ? "Synced" : "Pending"}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <IconButton
                        aria-label="View Details"
                        icon={<ViewIcon />}
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewDetails(blokInspeksi)}
                      />
                      <IconButton
                        aria-label="Toggle Sync Status"
                        icon={<FaSync />}
                        size="sm"
                        colorScheme={blokInspeksi.is_synced ? "orange" : "green"}
                        variant="outline"
                        onClick={() => handleUpdateSyncStatus(blokInspeksi, !blokInspeksi.is_synced)}
                        isLoading={submitLoading}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>

        {blokInspeksis.length === 0 && (
          <Alert status="info">
            <AlertIcon />
            No blok inspeksi data found matching your criteria.
          </Alert>
        )}
      </VStack>

      {/* Blok Inspeksi Details Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Blok Inspeksi Details
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedBlokInspeksi && (
              <VStack spacing={4} align="stretch">
                <HStack>
                  <Text fontWeight="bold" w="150px">UUID:</Text>
                  <Text fontFamily="mono" fontSize="sm">{selectedBlokInspeksi.uuid}</Text>
                </HStack>
                <HStack>
                  <Text fontWeight="bold" w="150px">Employee Name:</Text>
                  <Text>{selectedBlokInspeksi.nama || "-"}</Text>
                </HStack>
                <HStack>
                  <Text fontWeight="bold" w="150px">Employee ID:</Text>
                  <Text>{selectedBlokInspeksi.nomor_induk_pegawai || "-"}</Text>
                </HStack>
                <HStack>
                  <Text fontWeight="bold" w="150px">User UUID:</Text>
                  <Text fontFamily="mono" fontSize="sm">{selectedBlokInspeksi.user_uuid}</Text>
                </HStack>
                <HStack>
                  <Text fontWeight="bold" w="150px">Sync Source:</Text>
                  <Badge colorScheme="gray" variant="outline">
                    {selectedBlokInspeksi.sync_source}
                  </Badge>
                </HStack>
                <HStack>
                  <Text fontWeight="bold" w="150px">Last Updated:</Text>
                  <Text>
                    {new Date(selectedBlokInspeksi.updated_at).toLocaleString()}
                  </Text>
                </HStack>
                <HStack>
                  <Text fontWeight="bold" w="150px">Sync Status:</Text>
                  <Badge colorScheme={selectedBlokInspeksi.is_synced ? "green" : "orange"}>
                    {selectedBlokInspeksi.is_synced ? "Synced" : "Pending"}
                  </Badge>
                </HStack>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            <Button onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default BlokInspeksiManagement;
